import * as THREE from "three";
import { PhysXHandler } from "./PhysXWorker";
import { copyVec3 } from "../../../visuals/visual-utils";
import { BasePhysXService } from "./PhysXBaseService";
import { PhysXObject, buildPhysXObject } from "./PhysXObject";

export class PhysXScene {
  public camera: THREE.PerspectiveCamera;
  public scene: THREE.Scene = new THREE.Scene();
  public sceneWidth = 800; // Default width, can be set later
  public sceneHeight = 600; // Default height, can be set later
  private clock = new THREE.Clock();
  private targetFPS = 10;
  private frameInterval = 1000 / this.targetFPS;
  public services: BasePhysXService[] = [];
  public objects = new Map<string, PhysXObject>();
  public lastUpdate = 0;
  // earth: THREE.Mesh<THREE.BufferGeometry<THREE.NormalBufferAttributes>, THREE.Material | THREE.Material[], THREE.Object3DEventMap>;
  constructor(public handler: PhysXHandler) {
    this.camera = new THREE.PerspectiveCamera(
      75,
      this.sceneWidth / this.sceneHeight,
      0.1,
      2000000000
    );
    this.camera.position.set(0, 0, 10); // Move the camera higher    
    this.camera.up.set(0, 0, 1);
    this.camera.lookAt(0, 0, 0); // Focus on the center
    this.camera.updateProjectionMatrix(); // Update the camera matrix after changes
    // this.earth = PhysXPlanet.createSphereMesh();
    // this.scene.add(this.earth);
    this.services = [
      // new PhysXCameraProjection(this),
    ]
    this.startRenderingLoop();
  }

  public getService<T extends BasePhysXService>(partType: new (...args: any[]) => T): T {
    const part = this.services.find(p => p instanceof partType);
    if (part) {
      return part as T;
    } else {
      throw new Error(`Part of type ${partType.name} not found.`);
    }
  }


  public setSize(width: number, height: number) {
    this.sceneWidth = width;
    this.sceneHeight = height;
    this.camera.aspect = this.sceneWidth / this.sceneHeight;
    this.camera.updateProjectionMatrix();
  }

  private renderableObjects:PhysXObject[] = [];
  private startRenderingLoop() {
    let lastFrameTime = performance.now();

    const animate = (currentTime: number) => {
      if (currentTime - lastFrameTime < this.frameInterval) {
        requestAnimationFrame(animate);
        return;
      }

      lastFrameTime = currentTime;
      const delta = this.clock.getDelta();
      this.scene.updateMatrixWorld(true);
      this.services.forEach(part => part.render(delta));
      this.renderableObjects.forEach((object: PhysXObject) => object.actor!.render(delta));
      requestAnimationFrame(animate);
    };
    animate(performance.now());
  }

  updateObjects(objects: PhysXObject[]) {
    const objectsToProcess: PhysXObject[] = [...objects];
    let checkCounter = objects.length * 2; // Limit the number of iterations to prevent infinite loops
    while (objectsToProcess.length > 0) {
      if (checkCounter-- <= 0) {
        console.warn("Too many iterations in updateObjects, breaking to prevent infinite loop.");
        break;
      }
      const object = objectsToProcess.shift()!;
      if (this.objects.has(object._id)) {
        const existingObject = this.objects.get(object._id)!;
        existingObject.position = object.position;
        existingObject.rotation = object.rotation;
        const obj = existingObject.obj!;
        copyVec3(object.position, obj.position);
        copyVec3(object.rotation, obj.rotation);
        obj.updateMatrixWorld();
        continue;
      }
      if (object.parentId && !this.objects.has(object.parentId)) {
        objectsToProcess.push(object); // Re-add to the queue if parent is not found
        continue; // Skip this object if its parent is not found
      }
      this.objects.set(object._id, object);
      buildPhysXObject(object, this, this.objects);
      // object.obj = obj; // Store the THREE.Object3D in the PhysXObject
    }
    this.renderableObjects = Array.from(this.objects.values()).filter(obj => obj.actor);
    this.lastUpdate = performance.now();
  }

  replaceObjects(objects: PhysXObject[]) {
    for (const [id, object] of this.objects) {
      if (object.obj) {
        object.obj.removeFromParent();
      }
    }
    this.objects.clear();
    this.updateObjects(objects);
  }


  public dispose() {
    this.services.forEach(part => part.dispose());
  }

}